






















































import type { PropType } from 'vue';
import type { WithRefs } from 'vue-typed-refs';
import type { WithProperties } from 'vue-typed-properties';
import { MountingPortal, Portal } from 'portal-vue';
import { Fragment } from 'vue-frag';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import { nanoid } from 'nanoid';

import type { ClassNames } from '../../types';
import { PopperMixin } from '../../mixins';
import { hasSlot } from '../../util';
import SldsButton from '../Button/Button.vue';
import { PopoverSize } from './types';

type Refs = {
  popover: HTMLDivElement;
};

export default (
  PopperMixin as WithRefs<
    Refs,
    WithProperties<{ focusTrap: FocusTrap | null }, typeof PopperMixin>
  >
).extend({
  name: 'Popover',
  inheritAttrs: false,
  components: {
    MountingPortal,
    Portal,
    Fragment,
    SldsButton,
  },
  props: {
    uid: {
      type: String,
      default() {
        return nanoid();
      },
    },
    headerUid: {
      type: String,
      default() {
        return nanoid();
      },
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String as PropType<PopoverSize>,
      default: undefined,
      validator: (val) => Object.values(PopoverSize).includes(val),
    },
    header: {
      type: String,
      default: undefined,
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    bodyClass: {
      type: [String, Object, Array] as PropType<ClassNames>,
      default: undefined,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    // eslint-disable-next-line @typescript-eslint/ban-types
    activatorOn(): Record<string, Function> {
      if (this.clickable) {
        return {
          click: this.toggle,
        };
      }
      return {
        mouseenter: this.show,
        mouseleave: this.close,
      };
    },
    activatorAttrs(): Record<string, string> {
      return { id: this.activatorId };
    },
    activatorId(): string {
      return `activator-${this.uid}`;
    },
    bodyId(): string {
      return `body-${this.uid}`;
    },
  },
  watch: {
    isOpen(value: boolean) {
      if (value) {
        this.$nextTick(() => {
          const activator = document.getElementById(this.activatorId);
          if (activator === null) {
            throw new Error(
              `Activator with id: "${this.activatorId}" not found`
            );
          }
          this.createPopperInstance(activator, this.$refs.popover);
          const focusTrap = createFocusTrap(this.$refs.popover, {
            setReturnFocus: activator,
            allowOutsideClick: true,
          });
          focusTrap.activate();
          this.focusTrap = focusTrap;
          this.$emit('open');
        });
        return;
      }

      this.destroyPopperInstance();
      this.$emit('close');
      this.deactivateFocusTrap();
    },
  },
  created() {
    this.focusTrap = null;
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
  },
  methods: {
    show() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    },
    toggle() {
      this.isOpen = !this.isOpen;
    },
    deactivateFocusTrap() {
      if (this.focusTrap == null) {
        return;
      }
      this.focusTrap.deactivate();
      this.focusTrap = null;
    },
    hasSlot,
  },
});
